/**
 * Unified Color System — Single source of truth for KPI, Chart, Legend, and Badge colors.
 *
 * ALL values derive from CSS variables defined in index.css so they automatically
 * respect light/dark mode. No hardcoded hex values.
 *
 * ┌─────────────────────────────────────────────────────────┐
 * │  One semantic key → One color → Everywhere              │
 * │  KPI card = Chart fill = Legend dot = Status badge       │
 * └─────────────────────────────────────────────────────────┘
 *
 * Usage:
 *   import { colorSystem, kpiColorMap } from '@/design-system/colorSystem';
 *
 *   // Charts (Recharts)
 *   <Bar fill={colorSystem.primary.base} />
 *   <Area fill={colorSystem.primary.light} stroke={colorSystem.primary.base} />
 *
 *   // Legends
 *   <span style={{ backgroundColor: colorSystem.success.base }} />
 *
 *   // KPI mapping
 *   const c = colorSystem[kpiColorMap.hospitals]; // → colorSystem.primary
 */

// ─── Semantic color families ─────────────────────────────────
// Each family provides base (solid), light (10% bg), mid (20% border), dark (text).
// All reference CSS variables so dark mode Just Works™.

export const colorSystem = {
  primary: {
    /** Solid fill for chart bars, dots, lines */
    base: 'hsl(var(--primary))',
    /** 10% tinted background for KPI cards, area fills */
    light: 'hsl(var(--kpi-blue-bg))',
    /** 20% tinted border for KPI card borders */
    mid: 'hsl(var(--kpi-blue-border))',
    /** Saturated text/icon color */
    dark: 'hsl(var(--kpi-blue-icon))',
    /** 135° gradient for glass-style KPI cards */
    gradient: 'linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--primary) / 0.04))',
    /** Radial glow spot (top-right bloom) */
    glow: 'radial-gradient(circle at top right, hsl(var(--primary) / 0.18), transparent 60%)',
  },
  success: {
    base: 'hsl(var(--success))',
    light: 'hsl(var(--kpi-green-bg))',
    mid: 'hsl(var(--kpi-green-border))',
    dark: 'hsl(var(--kpi-green-icon))',
    gradient: 'linear-gradient(135deg, hsl(var(--success) / 0.15), hsl(var(--success) / 0.04))',
    glow: 'radial-gradient(circle at top right, hsl(var(--success) / 0.18), transparent 60%)',
  },
  warning: {
    base: 'hsl(var(--warning))',
    light: 'hsl(var(--kpi-orange-bg))',
    mid: 'hsl(var(--kpi-orange-border))',
    dark: 'hsl(var(--kpi-orange-icon))',
    gradient: 'linear-gradient(135deg, hsl(var(--warning) / 0.15), hsl(var(--warning) / 0.04))',
    glow: 'radial-gradient(circle at top right, hsl(var(--warning) / 0.18), transparent 60%)',
  },
  danger: {
    base: 'hsl(var(--destructive))',
    light: 'hsl(var(--kpi-red-bg))',
    mid: 'hsl(var(--kpi-red-border))',
    dark: 'hsl(var(--kpi-red-icon))',
    gradient: 'linear-gradient(135deg, hsl(var(--destructive) / 0.15), hsl(var(--destructive) / 0.04))',
    glow: 'radial-gradient(circle at top right, hsl(var(--destructive) / 0.18), transparent 60%)',
  },
  info: {
    base: 'hsl(var(--info))',
    light: 'hsl(var(--kpi-purple-bg))',
    mid: 'hsl(var(--kpi-purple-border))',
    dark: 'hsl(var(--kpi-purple-icon))',
    gradient: 'linear-gradient(135deg, hsl(var(--info) / 0.15), hsl(var(--info) / 0.04))',
    glow: 'radial-gradient(circle at top right, hsl(var(--info) / 0.18), transparent 60%)',
  },
  muted: {
    base: 'hsl(var(--muted-foreground))',
    light: 'hsl(var(--muted))',
    mid: 'hsl(var(--border))',
    dark: 'hsl(var(--muted-foreground))',
    gradient: 'linear-gradient(135deg, hsl(var(--muted-foreground) / 0.10), hsl(var(--muted-foreground) / 0.03))',
    glow: 'radial-gradient(circle at top right, hsl(var(--muted-foreground) / 0.10), transparent 60%)',
  },
} as const;

export type ColorKey = keyof typeof colorSystem;

// ─── KPI → Color mapping ────────────────────────────────────
// Maps domain concepts to semantic color families.
// Use: const c = colorSystem[kpiColorMap.hospitals]

export const kpiColorMap = {
  // Entities
  hospitals: 'primary',
  hcps: 'success',
  patients: 'info',
  programs: 'primary',

  // Case / workflow
  activeCases: 'warning',
  pendingTasks: 'danger',
  active: 'success',
  assistance: 'info',

  // Financial
  approved: 'primary',
  utilized: 'success',
  remaining: 'warning',
  paid: 'info',
  liability: 'danger',
} as const satisfies Record<string, ColorKey>;

export type KpiColorKey = keyof typeof kpiColorMap;

// ─── Chart color array ──────────────────────────────────────
// For charts that need an ordered palette (pie/donut slices, stacked bars).
// Cycle through these for multi-series charts.

export const chartPalette = [
  colorSystem.primary.base,
  colorSystem.success.base,
  colorSystem.warning.base,
  colorSystem.info.base,
  colorSystem.danger.base,
  colorSystem.muted.base,
] as const;

// ─── Legend helpers ─────────────────────────────────────────

export interface LegendItem {
  label: string;
  color: string;
}

/** Build a legend array from data keys using the kpiColorMap. */
export function buildLegend(entries: Array<{ key: KpiColorKey; label: string }>): LegendItem[] {
  return entries.map(({ key, label }) => ({
    label,
    color: colorSystem[kpiColorMap[key]].base,
  }));
}
